import { useState, useRef, useEffect, useCallback } from "react";
import { Bot, Send, Loader2, Sparkles, ChevronDown } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { api } from "@/lib/api";

const INTRO = {
  from: "allison",
  text: "Hi, I'm Allison — your internal operations co-pilot. I can help you summarize clients, draft replies and estimates, review organizers, flag missing documents, surface workflow bottlenecks and more. I work strictly within your own permissions and never expose anything you can't already see. What can I help with?",
};

// Stable per-tab session so multi-turn context is preserved.
const SESSION_ID = (() => {
  let s = sessionStorage.getItem("allison_session");
  if (!s) { s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem("allison_session", s); }
  return s;
})();

const CONF_STYLE = { High: "bg-emerald-50 text-emerald-700", Medium: "bg-amber-50 text-amber-700", Low: "bg-slate-100 text-slate-500" };

function RetrievalSummary({ summary, related, idx }) {
  const [open, setOpen] = useState(false);
  const s = summary || {};
  return (
    <div className="mt-1.5 rounded-xl border border-indigo-100 bg-indigo-50/40 overflow-hidden" data-testid={`allison-retrieval-summary-${idx}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`allison-retrieval-toggle-${idx}`}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-indigo-50 transition-colors">
        <Sparkles className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
        <span className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wide flex-1">Knowledge Retrieval Summary</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${CONF_STYLE[s.match_confidence] || CONF_STYLE.Low}`}>{s.match_confidence}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-indigo-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-1.5 text-[11px] text-slate-600" data-testid={`allison-retrieval-body-${idx}`}>
          {s.jurisdiction && <div><span className="font-semibold text-slate-500">Jurisdiction:</span> {s.jurisdiction}</div>}
          {(s.topics || []).length > 0 && <div><span className="font-semibold text-slate-500">Topics:</span> {s.topics.join(", ")}</div>}
          {(s.authority_chain || []).length > 0 && <div><span className="font-semibold text-slate-500">Authority:</span> {s.authority_chain.join(" → ")}</div>}
          <div><span className="font-semibold text-slate-500">Retrieval method:</span> {s.retrieval_method}</div>
          {(related || []).length > 0 && (
            <div>
              <span className="font-semibold text-slate-500">Related authorities:</span>
              <ul className="mt-0.5 space-y-0.5">
                {related.slice(0, 5).map((r, j) => (
                  <li key={j} className="text-slate-500">• {r.document_title} <span className="text-slate-400">({(r.relationship_type || "").replace(/_/g, " ")})</span></li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[10px] text-slate-400 italic pt-0.5">{s.note}</p>
        </div>
      )}
    </div>
  );
}

export default function AllisonPanel({ open, onOpenChange }) {
  const [messages, setMessages] = useState([INTRO]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const loadHistory = useCallback(() => {
    api.get(`/ai-agents/allison/history`, { params: { session_id: SESSION_ID } })
      .then((r) => {
        const msgs = r.data.messages || [];
        if (msgs.length) setMessages(msgs.map((m) => ({ from: m.role === "allison" ? "allison" : "user", text: m.text })));
      }).catch(() => {});
  }, []);

  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { from: "user", text }]);
    setInput("");
    setBusy(true);
    // Journey-aware: when Allison is opened inside a specific matter, pass the client_id
    // so she reasons over the canonical Case Context + Journey Evaluation for that contact.
    const workspaceClientId = () => {
      const p = window.location.pathname;
      let mt = p.match(/^\/crm\/([^/]+)$/);
      if (mt && !["leads", "active-clients", "inactive-clients"].includes(mt[1])) return mt[1];
      mt = p.match(/^\/client-workspace\/([^/]+)$/); if (mt) return mt[1];
      mt = p.match(/^\/case-analysis\/([^/]+)$/); if (mt) return mt[1];
      return null;
    };
    try {
      const r = await api.post(`/ai-agents/allison/chat`, {
        message: text, session_id: SESSION_ID,
        context: `Current page: ${window.location.pathname}`,
        client_id: workspaceClientId() || undefined,
      });
      setMessages((m) => [...m, { from: "allison", text: r.data.reply, retrieval: r.data.knowledge_retrieval_summary, related: r.data.related_sources }]);
    } catch (e) {
      setMessages((m) => [...m, { from: "allison", text: "Sorry — I couldn't process that just now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="allison-panel">
        <SheetHeader className="px-5 py-4 border-b border-slate-100">
          <SheetTitle className="flex items-center gap-2 font-heading">
            <span className="h-8 w-8 rounded-lg bg-charcoal flex items-center justify-center">
              <Bot className="h-4 w-4 text-brand-gold" />
            </span>
            Allison
            <span className="ml-1 text-[10px] font-medium uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="allison-status">
              Connected
            </span>
          </SheetTitle>
          <SheetDescription>Internal Operations AI Co-Pilot</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto tf-scroll px-5 py-4 space-y-4 bg-slate-50" data-testid="allison-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] ${m.from === "user" ? "" : "w-full"}`}>
                <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.from === "user"
                    ? "bg-charcoal text-white rounded-br-sm"
                    : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                  {m.text}
                </div>
                {m.from !== "user" && m.retrieval && m.retrieval.metadata_active && !m.retrieval.fallback && (
                  <RetrievalSummary summary={m.retrieval} related={m.related} idx={i} />
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start" data-testid="allison-typing">
              <div className="bg-white border border-slate-200 text-slate-400 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />Allison is thinking…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-slate-100 p-3 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask Allison…"
            disabled={busy}
            data-testid="allison-input"
            className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold disabled:opacity-60"
          />
          <button
            onClick={send}
            disabled={busy}
            data-testid="allison-send"
            className="bg-charcoal hover:bg-graphite text-white rounded-lg p-2.5 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
